import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Card, Icon } from "@mui/material";
import { keyframes } from "@emotion/react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import apiClient from "utils/apiClient";
import { getAuthToken } from "utils/authToken";
import { ENDPOINTS } from "utils/endpoints";
import { RED, GREEN } from "assets/theme/brandColors";

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-14px); }
  to   { opacity: 1; transform: translateX(0); }
`;

function Goals_Table({ recentGoalUpdates }) {
  const [userGoals, setUserGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  // Set of goal_ids that just completed — drives the flash highlight
  const [flashIds, setFlashIds] = useState(new Set());
  const [newIds, setNewIds] = useState(new Set());
  const flashTimerRef = useRef(null);
  const newTimerRef = useRef(null);
  // Hold pending updates until goals have loaded
  const pendingUpdatesRef = useRef(null);

  const applyUpdates = (updates) => {
    const justCompleted = new Set(updates.filter((u) => u.completed).map((u) => u.goal_id));
    const justAssigned = new Set(updates.filter((u) => u.new_goal_assigned).map((u) => u.goal_id));
    if (justCompleted.size > 0) {
      setFlashIds(justCompleted);
      clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setFlashIds(new Set()), 4000);
    }
    if (justAssigned.size > 0) {
      setNewIds(justAssigned);
      clearTimeout(newTimerRef.current);
      newTimerRef.current = setTimeout(() => setNewIds(new Set()), 700);
    }
  };

  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const res = await apiClient.get(ENDPOINTS.goals.me);
        setUserGoals(res.data);
        // Apply any updates that arrived before goals loaded
        if (pendingUpdatesRef.current) {
          applyUpdates(pendingUpdatesRef.current);
          pendingUpdatesRef.current = null;
        }
      } catch {
        setUserGoals([]);
      } finally {
        setLoading(false);
      }
    };
    if (!getAuthToken()) {
      setLoading(false);
      return;
    }
    fetchGoals();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchGoals();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      clearTimeout(flashTimerRef.current);
      clearTimeout(newTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!recentGoalUpdates || recentGoalUpdates.length === 0) return;
    if (loading) {
      // Goals not ready yet — defer until they load
      pendingUpdatesRef.current = recentGoalUpdates;
    } else {
      applyUpdates(recentGoalUpdates);
    }
  }, [recentGoalUpdates]);

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDBox display="flex" alignItems="center" gap={1} mb={2}>
          <MDTypography variant="h6" fontWeight="medium">
            Goals
          </MDTypography>
          <MDBox display="flex" alignItems="center" gap={0.5} ml={1}>
            <Icon sx={{ fontSize: 16, color: RED, mt: "-1px" }}>done</Icon>
            <MDTypography variant="caption" color="secondary">
              Earn XP by completing goals
            </MDTypography>
          </MDBox>
        </MDBox>

        {loading ? (
          [1, 2, 3].map((i) => (
            <MDBox key={i} py={1.5} sx={{ borderBottom: "1px solid #f5f5f5" }}>
              <MDBox sx={{ height: 7, bgcolor: "#f0f0f0", borderRadius: 4 }} />
            </MDBox>
          ))
        ) : userGoals.length === 0 ? (
          <MDTypography variant="body2" color="secondary">
            No active goals.
          </MDTypography>
        ) : (
          userGoals.map((goal, index) => {
            const countCompleted = Number(goal.count_completed) || 0;
            const target = Number(goal.target) || 1;
            const raw = (countCompleted / target) * 100;
            const progress = Number.isFinite(raw) ? Math.min(100, raw) : 0;
            const isDone = goal.status === "completed";
            const isFlashing = flashIds.has(goal.id);
            const isNew = newIds.has(goal.id);

            return (
              <MDBox
                key={goal.id ?? index}
                py={1.5}
                sx={{
                  borderBottom: index < userGoals.length - 1 ? "1px solid #f5f5f5" : "none",
                  opacity: isDone && !isFlashing ? 0.72 : 1,
                  borderRadius: isFlashing ? "8px" : 0,
                  bgcolor: isFlashing ? "rgba(67,160,71,0.08)" : "transparent",
                  mx: isFlashing ? -1 : 0,
                  px: isFlashing ? 1 : 0,
                  transition: "background-color 0.5s, opacity 0.3s",
                  animation: isNew ? `${slideIn} 0.45s ease both` : "none",
                }}
              >
                {/* Top row: icon + name + XP */}
                <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
                  <MDTypography variant="body2" sx={{ fontSize: "1.1rem", flexShrink: 0 }}>
                    {isDone ? "✅" : goal.icon || "🎯"}
                  </MDTypography>
                  <MDBox flex={1} minWidth={0}>
                    <MDBox display="flex" alignItems="center" gap={0.75}>
                      <MDTypography
                        variant="button"
                        fontWeight="regular"
                        color="text"
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 400,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "block",
                          textDecoration: isDone ? "line-through" : "none",
                          color: isDone ? "#888" : "inherit",
                        }}
                      >
                        {goal.title}
                      </MDTypography>
                      {isNew && (
                        <MDBox
                          component="span"
                          sx={{
                            fontSize: "9px",
                            fontWeight: 700,
                            color: "#fff",
                            bgcolor: "#7c3aed",
                            borderRadius: "4px",
                            px: "5px",
                            py: "1px",
                            letterSpacing: 0.5,
                            flexShrink: 0,
                            lineHeight: 1.6,
                          }}
                        >
                          NEW
                        </MDBox>
                      )}
                    </MDBox>
                    {goal.description && (
                      <MDTypography
                        variant="caption"
                        sx={{
                          fontSize: "0.8rem",
                          color: "#555",
                          display: "block",
                          lineHeight: 1.3,
                          mt: "1px",
                        }}
                      >
                        {goal.description}
                      </MDTypography>
                    )}
                  </MDBox>
                  {/* XP — shows flash label while flashing */}
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      color: isFlashing ? GREEN : isDone ? GREEN : RED,
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      transition: "color 0.4s",
                    }}
                  >
                    {isFlashing ? `+${goal.xp} XP ✓` : isDone ? "Earned!" : `+${goal.xp} XP`}
                  </MDTypography>
                </MDBox>

                {/* Bottom row: progress bar + count */}
                <MDBox display="flex" alignItems="center" gap={1} mt={0.75}>
                  <MDBox
                    flex={1}
                    sx={{ height: 7, bgcolor: "#f0f0f0", borderRadius: 4, overflow: "hidden" }}
                  >
                    <MDBox
                      sx={{
                        width: `${progress}%`,
                        height: "100%",
                        bgcolor: isDone ? GREEN : RED,
                        borderRadius: 4,
                        transition: "width 0.4s",
                      }}
                    />
                  </MDBox>
                  <MDTypography
                    variant="caption"
                    sx={{
                      fontSize: "11px",
                      whiteSpace: "nowrap",
                      minWidth: "32px",
                      color: isDone ? GREEN : "secondary",
                      fontWeight: isDone ? 700 : 400,
                    }}
                  >
                    {isDone ? `✓` : `${countCompleted}/${target}`}
                  </MDTypography>
                </MDBox>
              </MDBox>
            );
          })
        )}
      </MDBox>
    </Card>
  );
}

Goals_Table.propTypes = {
  recentGoalUpdates: PropTypes.arrayOf(
    PropTypes.shape({
      goal_id: PropTypes.number,
      completed: PropTypes.bool,
      new_goal_assigned: PropTypes.bool,
      xp_awarded: PropTypes.number,
    })
  ),
};

Goals_Table.defaultProps = {
  recentGoalUpdates: null,
};

export default Goals_Table;
